import React from "react"
import { useFormik } from "formik"
import Button from "@love-irish/core-button"
import { IconMail } from "@tabler/icons-react"

export type LeadType = "newsletter" | "waitlist" | "account"

interface LeadCopy {
  title: string
  description: string
  cta: string
  successTitle: string
  successBody: string
}

/**
 * Copy presets per lead type. Every path creates an invited (unverified) user who must confirm
 * their email to use the site — so the success copy names the *account*, not a "subscription",
 * to keep confirmation intent high. Explicit title/description/cta props override these.
 */
export const LEAD_COPY: Record<LeadType, LeadCopy> = {
  newsletter: {
    title: "Get Irish in your inbox",
    description:
      "New words, stories, and features — plus a free account to save your progress.",
    cta: "Get updates",
    successTitle: "Check your email to activate",
    successBody:
      "We sent a link to confirm your email and unlock your free account — confirm it to start saving words and tracking your streak.",
  },
  waitlist: {
    title: "Get early access",
    description: "Join the waitlist — we'll email you the moment it opens.",
    cta: "Join the waitlist",
    successTitle: "You're on the list",
    successBody:
      "Check your email to confirm and claim your spot. Confirming activates your account so you're ready when it launches.",
  },
  account: {
    title: "Create your free account",
    description: "Save words, track your streak, and unlock the full site.",
    cta: "Sign up with email",
    successTitle: "Almost there — check your email",
    successBody:
      "We sent a link to confirm your email and finish setting up your account.",
  },
}

export interface NewsletterSignupTemplateProps {
  /** Which lead flow this is — selects default copy (see LEAD_COPY). Default "newsletter". */
  leadType?: LeadType
  /** Heading shown inside the card (overrides the leadType default) */
  title?: string
  /** Subtext shown below the title (overrides the leadType default) */
  description?: React.ReactNode
  /** Submit button label (overrides the leadType default) */
  cta?: string
  /** Called with the validated email when the form is submitted */
  onSubmit: (email: string) => void
  /** Disables the form and shows a loading indicator on the button */
  loading?: boolean
  /** Switches to the success / check-your-email view */
  success?: boolean
  /** Base-level error string to display below the input */
  error?: string | null
}

const StatusIcon = ({ label }) => (
  <div className="relative inline-block">
    <span className="inline-block absolute bottom-0 right-0 -translate-y-1/4 translate-x-1/4 bg-background rounded-full p-1 text-lg">
      {label}
    </span>
    <IconMail
      className="inline-block mb-4 text-earth-900"
      size={44}
      stroke={2}
    />
  </div>
)

/**
 * Presentational email-lead signup card with a gradient border. Behind every submit is an invited
 * (unverified) user who confirms their email to use the site — pick a `leadType` to frame it.
 *
 * ## Usage
 * ```tsx
 * import { NewsletterSignupTemplate } from '@love-irish/newsletter-signup'
 *
 * <NewsletterSignupTemplate
 *   leadType="account"
 *   onSubmit={(email) => console.log(email)}
 * />
 * ```
 */
export const NewsletterSignupTemplate: React.FC<
  NewsletterSignupTemplateProps
> = ({
  leadType = "newsletter",
  title,
  description,
  cta,
  onSubmit,
  loading = false,
  success = false,
  error = null,
}) => {
  const copy = LEAD_COPY[leadType] ?? LEAD_COPY.newsletter
  const resolvedTitle = title ?? copy.title
  const resolvedDescription = description ?? copy.description
  const resolvedCta = cta ?? copy.cta

  // Hook must be called unconditionally (Rules of Hooks)
  const formik = useFormik({
    initialValues: { email: "" },
    validate: (values) => {
      const errors: Record<string, string> = {}
      if (!values.email) {
        errors.email = "Required"
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errors.email = "Invalid email address"
      }
      return errors
    },
    onSubmit: (values) => onSubmit(values.email),
  })

  return (
    <div className="rounded-lg p-2 bg-linear-to-br via-teal-300 from-earth-500 to-water-700">
      <div className="rounded bg-background text-foreground p-3">
        {success ? (
          <div className="p-5 text-center">
            <StatusIcon label="❤️" />
            <h3 className="text-lg font-bold">{copy.successTitle}</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              {copy.successBody}
            </p>
          </div>
        ) : (
          <form onSubmit={formik.handleSubmit} noValidate>
            <div className="p-5 pb-0 text-center">
              <StatusIcon label="☘️" />
              <h3 className="text-lg font-bold">{resolvedTitle}</h3>
              {resolvedDescription && (
                <p className="text-muted-foreground mt-1 text-sm">
                  {resolvedDescription}
                </p>
              )}
            </div>

            <div className="mt-4 p-5 pt-4">
              <div>
                <label htmlFor="newsletter-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  aria-label="Email address"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                  className="w-full px-4 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
                {formik.touched.email && formik.errors.email && (
                  <p
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {formik.errors.email}
                  </p>
                )}
                {error && (
                  <p
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {error}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="mt-3 w-full"
                disabled={loading}
                loading={loading}
              >
                {loading ? "Sending..." : resolvedCta}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default NewsletterSignupTemplate
